/* eslint-disable camelcase */

import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { results, PlainTable } from '@mdc/ui';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import ADDRESS_DATA from './AddressOverviewData';
import { dayjs, Authenticator, Base64 } from '@mdc/services';
import { SCAN_ADDRESS_STATUS } from '@mdc/constants';

import './AddressOverview.scss';

const { ScoreHeader, PlainTablePlaceholder, CodeSnippet } = results;

const AddressOverview = ({ type, id, addressScanResult, resultsLoading, fileImage }) => {
    const { t, ready } = useTranslation();
    const apiKey = Authenticator.apikey;

    const scanResults = addressScanResult?.lookup_results || {};

    const scoreMetascanDom = useMemo(() => {
        const scanProcessText = resultsLoading ? t('Loading') : '';

        return <ScoreHeader
            category={t('Multiscan Score')}
            score={scanResults.detected_by}
            info={scanResults.detected_by > 0 ? t('Threats detected') : t('No threats detected')}
            detection={scanResults.detected_by > 0 ? 'detected' : 'notDetected'}
            numberOfEngines={scanResults.sources?.length}
            scoreText={t('ENGINES')}
            isProcessing={resultsLoading}
            processImgSrc={fileImage.processing.publicURL}
            processingText={scanProcessText}
        />;
    }, [resultsLoading, scanResults, t]);

    const aboutMultiscanningDom = (
        /*eslint-disable-next-line no-useless-escape*/
        <p dangerouslySetInnerHTML={{ __html: t('Reputation intelligence data collected from multiple vendors. Please read our <a href=\"https://onlinehelp.opswat.com/mdcloud/2._Analyzing_IPs_with_MetaDefender_Cloud.html\" rel=\"noopener noreferrer\" target=\"_blank\">documentation</a> for details about interpreting the results') }}/>
    );

    const tableData = useMemo(() => {
        const sources = scanResults?.sources;
        if (sources) {
            let data = sources.map((source) => {
                const provider = source.provider;
                let assessment = source.assessment;
                let threatClass, iconClass, sorting;

                switch (source.status) {
                    case SCAN_ADDRESS_STATUS.DIRTY:
                    case SCAN_ADDRESS_STATUS.BLACKLISTED:
                        threatClass = 'detected';
                        iconClass = 'icon-close';
                        sorting = 0;
                        break;
                    case SCAN_ADDRESS_STATUS.FAILED_TO_SCAN:
                        assessment = t('Failed to scan');
                        threatClass = 'warning';
                        iconClass = 'icon-warning-empty';
                        sorting = 1;
                        break;
                    case SCAN_ADDRESS_STATUS.WHITELISTED:
                        assessment = t('Trustworthy');
                        threatClass = '';
                        iconClass = 'icon-checkmark-symbol';
                        sorting = 2;
                        break;
                    case SCAN_ADDRESS_STATUS.UNKNOWN:
                    default:
                        assessment = t('No threat detected');
                        threatClass = '';
                        iconClass = 'icon-checkmark-symbol';
                        sorting = 3;
                        break;
                }

                return {
                    result: (<span className={threatClass}><i className={`icon ${iconClass}`}/>{assessment}</span>),
                    source: (<span className={threatClass}>{provider}</span>),
                    lastDetected: source['detect_time'] ? dayjs(source['detect_time']).format('ll') : '-',
                    lastUpdate: source['update_time'] ? dayjs(source['update_time']).format('ll') : '-',
                    sorting
                };
            });

            data.sort((scan1, scan2) => {
                if (scan1.sorting > scan2.sorting) {
                    return 1;
                }

                return scan1.sorting < scan2.sorting ? -1 : 0;
            });
            return data;
        }
    }, [scanResults, t]);

    const resultEnginesTableDom = useMemo(() => {
        if (!tableData || !addressScanResult || resultsLoading) {
            return <PlainTablePlaceholder numberOfColumns={4}/>;
        }

        return <PlainTable
            columnsData={ADDRESS_DATA(t)}
            data={tableData}
            hasSorting={true}
        />;

    }, [tableData, scanResults, resultsLoading]);

    const codeDOM = useMemo(() => {
        return <CodeSnippet apiKey={apiKey} id={encodeURIComponent(Base64.decode(id))} type={type}/>;
    }, [apiKey, id, type]);

    if (!ready) {
        return null;
    }

    return <section className='addressOverview'>
        <Row>
            <Col lg={3} md={3} xs={12}>
                {scoreMetascanDom}
                {aboutMultiscanningDom}
            </Col>
            <Col lg={9} md={9} xs={12}>
                {resultEnginesTableDom}
            </Col>
        </Row>
        <Row className='codeSnippetRow'>
            <Col>
                {codeDOM}
            </Col>
        </Row>
    </section>;
};

AddressOverview.propTypes = {
    type: PropTypes.string,
    id: PropTypes.string,
    addressScanResult: PropTypes.object,
    resultsLoading: PropTypes.bool,
    fileImage: PropTypes.object.isRequired,
};

export default AddressOverview;
